import { getBoxProgramColorTypeWithHex } from "@/modules/boxProgrammation/boxProgramUtils"
import { boxProgramColors } from "@/modules/boxProgrammation/components/boxProgramColors"
import type { BoxSlotParticipation } from "@/modules/boxSlot/boxSlotTypes"
import type { BoxSlotFormikType } from "@/modules/boxSlot/formik/boxSlotFormikTypes"
import type { BoxSlotDetailsDto, BoxSlotItemDto } from "@/modules/boxSlot/gateway/boxSlotDto"
import { CalendarEventTypes, type CalendarEventTypeWithHour } from "@/modules/calendar/calendarTypes"
import { UserEntity } from "@/modules/profile/userProfile/userEntity"
import { v4 as uuidv4 } from "uuid"

export type BoxSlotItemState = Omit<BoxSlotItemEntity, "setParticipantLimit" | "toCalendarEventTypeWithHour">

export class BoxSlotItemEntity {
  id: string
  color: string
  programmationId: string
  programmationName: string
  startDateTime: Date
  endDateTime: Date
  registeredParticipantCount: number
  pendingParticipantCount: number
  participantLimit: number
  sessionId: string | null
  location: string | null
  participation: BoxSlotParticipation | null
  allowMultiBooking: boolean | null
  dropinsAccepted: boolean
  trialsAccepted: boolean
  assignedStaffMembers: UserEntity[]

  private constructor(state: BoxSlotItemState) {
    this.id = state.id
    this.color = state.color
    this.programmationId = state.programmationId
    this.programmationName = state.programmationName
    this.startDateTime = state.startDateTime
    this.endDateTime = state.endDateTime
    this.registeredParticipantCount = state.registeredParticipantCount
    this.pendingParticipantCount = state.pendingParticipantCount
    this.participantLimit = state.participantLimit
    this.sessionId = state.sessionId
    this.location = state.location
    this.participation = state.participation
    this.allowMultiBooking = state.allowMultiBooking
    this.assignedStaffMembers = state.assignedStaffMembers
    this.dropinsAccepted = state.dropinsAccepted
    this.trialsAccepted = state.trialsAccepted
  }

  static fromDto(dto: BoxSlotItemDto): BoxSlotItemEntity {
    return new BoxSlotItemEntity({
      id: dto.id,
      color: dto.color,
      programmationId: dto.programId,
      programmationName: dto.programName,
      startDateTime: new Date(dto.startTime),
      endDateTime: new Date(dto.endTime),
      registeredParticipantCount: dto.registeredParticipantCount,
      pendingParticipantCount: dto.pendingParticipantCount,
      participantLimit: dto.participantLimit,
      sessionId: dto.sessionId,
      location: dto.location,
      participation: dto.participation,
      allowMultiBooking: dto.allowMultiBooking,
      assignedStaffMembers: dto.assignedStaffMembers.map((staff) => UserEntity.fromDto(staff)),
      dropinsAccepted: dto.dropinsAccepted,
      trialsAccepted: dto.trialsAccepted,
    })
  }

  toCalendarEventTypeWithHour(): CalendarEventTypeWithHour {
    const colorType = getBoxProgramColorTypeWithHex(this.color)
    const programationColor = boxProgramColors[colorType]

    return {
      id: this.id,
      programmationId: this.programmationId,
      title: this.programmationName,
      date: this.startDateTime.toISOString(),
      startDate: this.startDateTime.toISOString(),
      endDate: this.endDateTime.toISOString(),
      editable: false,
      leftBorderColor: programationColor.background,
      borderStyle: this.sessionId ? "solid" : "dashed",
      _type: CalendarEventTypes.CALENDAR_EVENT_WITH_HOUR,
      assignedStaffMembers: this.assignedStaffMembers,
      color: this.sessionId ? "var(--neutral-50)" : "var(--surface-background)",
      borderColor: "var(--neutral-300)",
      textColor: "var(--neutral-900)",
      participantLimit: this.participantLimit,
    }
  }
}

export type BoxSlotDraftState = Omit<BoxSlotDraftEntity, "toCalendarEventTypeWithHour">

export class BoxSlotDraftEntity {
  id?: string
  color: string
  programmationId: string
  programmationName: string
  startDateTime: string | null
  endDateTime: string
  participantLimit: number
  contentEarlyAccess: boolean
  location?: string
  participation?: BoxSlotParticipation
  allowMultiBooking: boolean
  assignedStaffMembers?: UserEntity[]
  dropinsAccepted: boolean
  trialsAccepted: boolean

  public constructor(state: BoxSlotDraftState) {
    this.id = state.id
    this.color = state.color
    this.programmationName = state.programmationName
    this.programmationId = state.programmationId
    this.startDateTime = state.startDateTime
    this.endDateTime = state.endDateTime
    this.participantLimit = state.participantLimit
    this.contentEarlyAccess = state.contentEarlyAccess
    this.location = state.location
    this.participation = state.participation
    this.allowMultiBooking = state.allowMultiBooking
    this.assignedStaffMembers = state.assignedStaffMembers
    this.dropinsAccepted = state.dropinsAccepted
    this.trialsAccepted = state.trialsAccepted
  }

  static toFormik(slot: BoxSlotDraftEntity): BoxSlotFormikType {
    return {
      programmationId: slot.programmationId,
      startDateTime: slot.startDateTime ? new Date(slot.startDateTime) : null,
      endDateTime: slot.endDateTime ? new Date(slot.endDateTime) : null,
      participantLimit: slot.participantLimit,
      contentEarlyAccess: slot.contentEarlyAccess,
      location: slot.location ?? "",
      limitStart: slot.participation?.limitStart ?? undefined,
      limitEnd: slot.participation?.limitEnd ?? undefined,
      limitCancel: slot.participation?.limitCancel ?? undefined,
      allowMultiBooking: slot.allowMultiBooking,
      assignedStaffMembers: slot.assignedStaffMembers,
      dropinsAccepted: slot.dropinsAccepted,
      trialsAccepted: slot.trialsAccepted,
    }
  }

  toCalendarEventTypeWithHour(): CalendarEventTypeWithHour {
    const colorType = getBoxProgramColorTypeWithHex(this.color)
    const programationColor = boxProgramColors[colorType]

    return {
      id: this.id ?? uuidv4(),
      programmationId: this.programmationId,
      title: this.programmationName,
      date: this.startDateTime ?? this.endDateTime,
      startDate: this.startDateTime ?? this.endDateTime,
      endDate: this.endDateTime,
      editable: false,
      borderColor: programationColor.background,
      leftBorderColor: programationColor.background,
      borderStyle: "dashed",
      _type: CalendarEventTypes.CALENDAR_EVENT_WITH_HOUR,
      assignedStaffMembers: this.assignedStaffMembers ?? [],
      participantLimit: this.participantLimit,
    }
  }
}

export type BoxSlotDetailsState = Omit<BoxSlotDetailsEntity, "setParticipantLimit">
export class BoxSlotDetailsEntity {
  id: string
  color: string
  programmationId: string // Coté session on parle de programId
  programmationName: string
  startDateTime: Date
  endDateTime: Date
  registeredParticipantCount: number
  pendingParticipantCount: number
  participantLimit: number
  sessionId: string | null
  location: string | null
  participation: BoxSlotParticipation | null
  allowMultiBooking: boolean
  assignedStaffMembers: UserEntity[]
  dropinsAccepted: boolean
  trialsAccepted: boolean
  contentEarlyAccess: boolean

  private constructor(state: BoxSlotDetailsState) {
    this.id = state.id
    this.color = state.color
    this.programmationId = state.programmationId
    this.programmationName = state.programmationName
    this.startDateTime = state.startDateTime
    this.endDateTime = state.endDateTime
    this.registeredParticipantCount = state.registeredParticipantCount
    this.pendingParticipantCount = state.pendingParticipantCount
    this.participantLimit = state.participantLimit
    this.sessionId = state.sessionId
    this.location = state.location
    this.participation = state.participation
    this.allowMultiBooking = state.allowMultiBooking
    this.assignedStaffMembers = state.assignedStaffMembers
    this.dropinsAccepted = state.dropinsAccepted
    this.trialsAccepted = state.trialsAccepted
    this.contentEarlyAccess = state.contentEarlyAccess
  }

  static fromDto(dto: BoxSlotDetailsDto): BoxSlotDetailsEntity {
    return new BoxSlotDetailsEntity({
      id: dto.id,
      color: dto.color,
      programmationId: dto.programId,
      programmationName: dto.programName,
      startDateTime: new Date(dto.startTime),
      endDateTime: new Date(dto.endTime),
      sessionId: dto.sessionId,
      location: dto.location,
      participation: dto.participation,
      allowMultiBooking: dto.allowMultiBooking,
      registeredParticipantCount: dto.registeredParticipantCount,
      pendingParticipantCount: dto.pendingParticipantCount,
      participantLimit: dto.participantLimit,
      assignedStaffMembers: dto.assignedStaffMembers.map((staff) => UserEntity.fromDto(staff)),
      dropinsAccepted: dto.dropinsAccepted,
      trialsAccepted: dto.trialsAccepted,
      contentEarlyAccess: dto.contentEarlyAccess,
    })
  }
}
